/**
 * Shared types + pure helpers for the dashboard filter panel.
 *
 * Extracted from DashboardFilters.tsx to keep that component under the 500-line
 * limit (CR-6352 review). Everything here is framework-free and unit-testable.
 */

import type { DashboardFilterState } from './types';

/**
 * The dashboard filter panel's filter object. The single source of truth is
 * `DashboardFilterState` in ./types; it's re-exported here under the
 * `FilterState` name the panel modules already use, so the two definitions
 * can't drift apart (CR-6352 review).
 */
export type FilterState = DashboardFilterState;

/**
 * CR-6401: filter keys that narrow the result set and so count toward the
 * "N filters active" badge. Dates are excluded because a range is always set
 * (the panel defaults to last 12 months), as are `currency` and `dataMode`,
 * which change how results are expressed rather than which rows are returned.
 */
const NARROWING_FILTER_KEYS: Array<keyof FilterState> = [
  'supplier', 'customer', 'site', 'supplierGroup', 'supplierType',
  'commodityGroup', 'commodityType', 'siteCountry', 'supplierCountry',
  'supplierCode', 'warehouse', 'division',
  'approvalStage', 'riskCategory',
];

/**
 * Count the filters currently narrowing the dashboard.
 *
 * @param filters - the panel's current filter state
 * @returns how many narrowing filters hold a non-default value
 */
export function countActiveFilters(filters: FilterState): number {
  const selected = NARROWING_FILTER_KEYS.filter((key) => {
    const value = filters[key];
    return typeof value === 'string' && value !== '';
  }).length;
  return selected + (filters.strategicOnly ? 1 : 0);
}

/** Human labels for the quick-range presets, used in the footer summary. */
const QUICK_RANGE_LABELS: Record<string, string> = {
  last12Months: 'last 12 months',
  thisMonth: 'this month',
  lastMonth: 'last month',
  lastQuarter: 'last quarter',
  thisYear: 'this year',
  lastYear: 'last year',
  // CR-5454: Cost tab only.
  next20Weeks: 'next 20 weeks',
};

/**
 * Describe the active date range for the filter footer.
 *
 * @param activeQuickRange - the matched preset key, or null for a custom range
 * @param isLast12MonthsActive - whether the dates match the default range
 * @param filters - current filter state, used to render a custom range
 * @returns a phrase to follow "Showing results for "
 */
export function describeActiveRange(
  activeQuickRange: string | null,
  isLast12MonthsActive: boolean,
  filters: FilterState,
): string {
  if (isLast12MonthsActive) return QUICK_RANGE_LABELS['last12Months'];
  if (activeQuickRange && QUICK_RANGE_LABELS[activeQuickRange]) {
    return QUICK_RANGE_LABELS[activeQuickRange];
  }
  if (filters.dateFrom && filters.dateTo) return `${filters.dateFrom} to ${filters.dateTo}`;
  if (filters.dateFrom) return `${filters.dateFrom} onwards`; // CR-1421: All future weeks
  return 'all dates';
}

/** Format a Date as YYYY-MM-DD in local time (avoids UTC timezone shift). */
export function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Computes the "Last 12 Months" date range: 12 complete months up to end of last month.
 * E.g. if today is April 9, 2026, returns { dateFrom: '2025-04-01', dateTo: '2026-03-31' }.
 */
/**
 * The `date_to` the dashboard actually queries. Historical mode (CR-4339)
 * excludes the current, incomplete month: the end is capped at the end of the
 * previous month (and defaults to it when no end is set). Live mode sends the
 * picker's end as-is. Every drill-down opened from a tile MUST use this same
 * value (METRIC-DEFINITIONS W3-T5): a ring frozen at 31 Aug and a grid that
 * runs to 30 Sep read two different arrears terms.
 *
 * @param dateTo - the picker's end date (ISO yyyy-mm-dd) or empty
 * @param dataMode - the Historical / Live switch
 * @param now - injectable clock for tests
 * @returns the ISO end date to send, or undefined when Live has no end set
 */
export function effectiveDateTo(
  dateTo: string | undefined,
  dataMode: 'historical' | 'live' | undefined,
  now: Date = new Date(),
): string | undefined {
  if (dataMode === 'live') return dateTo || undefined;
  const endOfPrevMonth = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 0));
  const userDateTo = dateTo || endOfPrevMonth;
  return userDateTo < endOfPrevMonth ? userDateTo : endOfPrevMonth;
}

/**
 * The mode-dependent query params a drill-down opened from a dashboard tile
 * must carry so it reads the SAME arrears term and the SAME window as the
 * tile (W3-T5): the capped `date_to` and, in Live mode, `data_mode=live`.
 * Spread it into the drill filters after `date_from`.
 *
 * @param filters - the dashboard filter state (or undefined before it loads)
 * @param now - injectable clock for tests
 * @returns `{ date_to?, data_mode? }`
 */
export function drilldownModeParams(
  filters: Partial<Pick<FilterState, 'dateTo' | 'dataMode'>> | undefined,
  now: Date = new Date(),
): { date_to?: string; data_mode?: 'live' } {
  const dateTo = effectiveDateTo(filters?.dateTo, filters?.dataMode, now);
  return {
    ...(dateTo && { date_to: dateTo }),
    ...(filters?.dataMode === 'live' && { data_mode: 'live' as const }),
  };
}

/**
 * CR-5454: the Cost tab's "Next 20 weeks" — Monday of the current week through
 * the Sunday twenty weeks after it (the current week plus the next 20), as the
 * original platform showed it: e.g. Mon 4 May → Sun 27 Sep.
 *
 * @param now - reference date (defaults to today)
 * @returns the range as local YYYY-MM-DD strings
 */
export function getNext20WeeksRange(now: Date = new Date()): { dateFrom: string; dateTo: string } {
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const end = new Date(monday);
  end.setDate(end.getDate() + 21 * 7 - 1);
  return { dateFrom: formatLocalDate(monday), dateTo: formatLocalDate(end) };
}

export function getLast12MonthsRange(): { dateFrom: string; dateTo: string } {
  const today = new Date();
  const dateFrom = formatLocalDate(new Date(today.getFullYear() - 1, today.getMonth(), 1));
  const dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0));
  return { dateFrom, dateTo };
}

/** Resolve a named quick-range to its {dateFrom, dateTo} (null when unknown). */
export function getQuickRangeDates(range: string): { dateFrom: string; dateTo: string } | null {
  const today = new Date();
  switch (range) {
    case 'last12Months': return getLast12MonthsRange();
    case 'next20Weeks': return getNext20WeeksRange();
    case 'thisMonth': return {
      dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    };
    case 'lastMonth': return {
      dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)),
    };
    case 'lastQuarter': {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const lqy = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const aq = lq < 0 ? 3 : lq;
      return {
        dateFrom: formatLocalDate(new Date(lqy, aq * 3, 1)),
        dateTo: formatLocalDate(new Date(lqy, (aq + 1) * 3, 0)),
      };
    }
    case 'thisYear': return {
      dateFrom: formatLocalDate(new Date(today.getFullYear(), 0, 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
    };
    case 'lastYear': return {
      dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, 0, 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear() - 1, 11, 31)),
    };
    default: return null;
  }
}

/** Per-surface additions to the shared drill scope. Everything else is common. */
export interface OtifDrillOptions {
  /**
   * CR-5868: on the supplier portal the panel's Site is a CUSTOMER company, so
   * it travels as `customer`. Sent as `site` it was overwritten by the drill
   * modal's view-context site (the supplier's own), and the pick was ignored.
   */
  isSupplier?: boolean;
  /**
   * Group Detail only: a clicked site bar is one customer site, and scopes the
   * drill to that `customer`, overriding the panel's customer filter.
   */
  clickedCustomerId?: number | null;
  /**
   * CR-7751: the dashboard's order-type toggle — the tile reads it from there,
   * not from the filter panel, so the drill takes it from the same place.
   * `all` sends nothing.
   */
  orderType?: 'all' | 'scheduled' | 'spot';
  /** Injectable clock, for tests. */
  now?: Date;
}

/**
 * CR-6405: THE scope every OTIF drill carries — the one builder all four
 * badge counts and all four grids are reached through.
 *
 * Before this, three tabs hand-built their own copy of this object and the
 * copies drifted: Strategic Suppliers Only reached only the Default tab,
 * Division reached only Site KPI, and the call-off basis reached neither of the
 * other two. A filter that narrows the ring but not the drill opened from it is
 * the CR-6395 defect — the badge and its grid describe a different population
 * than the chart that was clicked. Keeping one builder makes badge = grid =
 * chart by construction rather than by three files agreeing by hand.
 *
 * Mirrors the TILE query in `useDashboardData` field for field (that is the
 * thing the drill has to agree with), including its rule that Division must not
 * also travel as `group` once a site is chosen.
 *
 * CR-7751: the commodity and supplier-code filters are forwarded too — every
 * drill-down now applies them with the tile's PO-header rule
 * (`purchasing/services/dashboard_po_filters.py`); before, a ring filtered to
 * one supplier code opened onto every supplier's lines.
 *
 * @param filters - the dashboard filter panel's state
 * @param opts - the per-surface additions (see OtifDrillOptions)
 * @returns drill query params in the API's key names
 */
export function buildOtifDrillFilters(
  filters: Partial<FilterState> | undefined,
  opts: OtifDrillOptions = {},
): Record<string, string> {
  const { isSupplier = false, clickedCustomerId = null, orderType, now = new Date() } = opts;
  const out: Record<string, string> = {};

  if (!filters) {
    if (clickedCustomerId != null) out['customer'] = String(clickedCustomerId);
    return out;
  }

  if (filters.dateFrom) out['date_from'] = filters.dateFrom;
  // W3-T5: the Historical/Live switch decides the arrears TERM and caps the
  // window; the drill must read the same term and window as the ring it opens.
  const mode = drilldownModeParams(filters, now);
  if (mode.date_to) out['date_to'] = mode.date_to;
  if (mode.data_mode) out['data_mode'] = mode.data_mode;

  if (filters.supplier) out['supplier'] = String(filters.supplier);
  if (filters.supplierGroup) out['supplier_group'] = String(filters.supplierGroup);

  const customer = clickedCustomerId != null
    ? String(clickedCustomerId)
    : (filters.customer ? String(filters.customer) : '');
  if (customer) out['customer'] = customer;

  // Must stay AFTER customer: on the supplier portal the site IS the customer
  // scope and deliberately wins over the panel's customer value (CR-5868).
  if (filters.site) out[isSupplier ? 'customer' : 'site'] = String(filters.site);

  // CR-7229: the warehouse scope has to reach the drill-down too, or the
  // detail list shows rows the tile it was opened from excluded.
  if (filters.warehouse) out['warehouse'] = String(filters.warehouse);
  if (filters.siteCountry) out['site_country'] = filters.siteCountry;
  if (filters.supplierCountry) out['supplier_country'] = filters.supplierCountry;
  // Customer review 11 Sep (item 34): Internal / External reaches the drills.
  if (filters.supplierType) out['supplier_type'] = filters.supplierType;
  // CR-6354: Strategic Suppliers Only, so the drill matches the filtered tile.
  if (filters.strategicOnly) out['strategic_only'] = 'true';
  // CR-7751: the tile's PO-header filters, under the tile's names.
  if (filters.commodityGroup) out['lines__commodity_group'] = String(filters.commodityGroup);
  if (filters.commodityType) out['lines__commodity_type'] = String(filters.commodityType);
  if (filters.supplierCode) out['supplier_code'] = filters.supplierCode;
  if (orderType && orderType !== 'all') out['order_type'] = orderType;
  // A basis toggle rather than a filter: it selects which call-off columns the
  // grid renders. It changes no count, but it must still agree across surfaces.
  if (filters.callOffWarehouseBasis) out['call_off_warehouse_basis'] = 'true';
  // CR-4349 / CR-5868: Division travels as `group` (the backend's key for it).
  // Sent even alongside `site`, unlike the TILE query, which drops it there
  // (CR-5454). The two endpoints resolve the pair differently and the
  // difference is deliberate: dashboard_stats lets group win, so sending both
  // widened a site user's tiles; the drill-down instead picks by `view_level`
  // (drilldown/views/scoping.py `_apply_view_level_scope`) and, on the supplier
  // portal, tries site before group. Withholding `group` here would strip the
  // scope from a GROUP-level drill rather than protect a site-level one.
  if (filters.division) out['group'] = String(filters.division);

  return out;
}

/**
 * CR-6405: the OTIF drill's per-surface parameter additions, declared here
 * rather than at each call site — the second half of "one shared builder, with
 * any tab specific additions declared in a single place".
 *
 * A month BAR is a bucket, and `grain` is what tells the backend so: under
 * W3-T7 a sub-month bucket carries no frozen arrears stock in Historical, so a
 * point and the rows it opens have to agree about which bucket was clicked.
 * The Default tab sent it; the Site KPI tab's identical month bar did not —
 * the same drift in miniature, now impossible because both call this.
 *
 * Pass `range: null` for a bar that is not a month (the Site KPI by-site
 * charts click through with a site name): the scope is returned untouched, and
 * no grain is claimed.
 *
 * @param base - the shared scope from `buildOtifDrillFilters`
 * @param range - the clicked month's window, or null if the bar is not a month
 * @returns the scope plus that month's window and grain
 */
export function otifMonthBucketFilters(
  base: Record<string, string>,
  range: { date_from: string; date_to: string } | null,
): Record<string, string> {
  if (!range) return base;
  return { ...base, date_from: range.date_from, date_to: range.date_to, grain: 'month' };
}

/**
 * Drill filters for the Group Detail KPI tab (W3-T5 + R-158). The drill API
 * reads `supplier` / `customer` / `site` — the tab used to send `supplier_id`
 * / `customer_id` / `site_id`, which the API dropped, so its OTIF drill was
 * unscoped. A clicked site bar scopes the drill to that site's `customer`
 * (the bar is one customer site), overriding the panel's customer filter.
 *
 * CR-6405: now a thin alias for `buildOtifDrillFilters` — this tab's only
 * difference from the others was the clicked-bar override, and every other
 * difference it had was drift.
 *
 * @param orderType - the dashboard's order-type toggle (CR-7751), as the tile reads it
 */
export function groupDetailDrillFilters(
  filters: Partial<FilterState> | undefined,
  clickedCustomerId?: number | null,
  now: Date = new Date(),
  orderType?: OtifDrillOptions['orderType'],
): Record<string, string> {
  return buildOtifDrillFilters(filters, { clickedCustomerId, now, orderType });
}

/**
 * Compute the date range + dataMode a quick-range button should apply.
 * "thisMonth" is the only Live range; every other range is Historical.
 * Returns null for an unknown range name.
 */
export function computeQuickDateRange(
  range: string,
): { dateFrom: string; dateTo: string; dataMode: 'historical' | 'live' } | null {
  const today = new Date();
  if (range === 'thisMonth') {
    return {
      dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      dataMode: 'live',
    };
  }
  let dateFrom = '';
  let dateTo = '';
  switch (range) {
    case 'lastMonth':
      dateFrom = formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
      dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0));
      break;
    case 'thisQuarter': {
      const currentQuarter = Math.floor(today.getMonth() / 3);
      dateFrom = formatLocalDate(new Date(today.getFullYear(), currentQuarter * 3, 1));
      dateTo = formatLocalDate(new Date(today.getFullYear(), (currentQuarter + 1) * 3, 0));
      break;
    }
    case 'lastQuarter': {
      const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
      const lastQuarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
      dateFrom = formatLocalDate(new Date(lastQuarterYear, adjustedQuarter * 3, 1));
      dateTo = formatLocalDate(new Date(lastQuarterYear, (adjustedQuarter + 1) * 3, 0));
      break;
    }
    case 'thisYear':
      dateFrom = formatLocalDate(new Date(today.getFullYear(), 0, 1));
      dateTo = formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
      break;
    case 'lastYear':
      dateFrom = formatLocalDate(new Date(today.getFullYear() - 1, 0, 1));
      dateTo = formatLocalDate(new Date(today.getFullYear() - 1, 11, 31));
      break;
    case 'last12Months': {
      const r = getLast12MonthsRange();
      dateFrom = r.dateFrom;
      dateTo = r.dateTo;
      break;
    }
    case 'next20Weeks': {
      // CR-5454: open orders are today's stock whatever the Data switch says,
      // so the forward range keeps the Historical default rather than forcing
      // Live the way This month does.
      const r = getNext20WeeksRange();
      dateFrom = r.dateFrom;
      dateTo = r.dateTo;
      break;
    }
    default:
      return null;
  }
  return { dateFrom, dateTo, dataMode: 'historical' };
}

// Split out to stay under the 500-line limit; re-exported for existing imports.
export { countryDisplayNames, deriveCountryOptions } from './countryOptions';

/**
 * The Default tab's drill-down filters: every dashboard filter, under the names
 * the dashboard request uses, so a drill-down lists exactly what the tile it
 * opens from counted.
 *
 * CR-7776 / CR-7751: the PO-header filters (order type, commodity, supplier
 * code) go under the tile's own parameter names, and every drill-down applies
 * them with the tile's rule — NCRs via `quality.services.ncr_dashboard_filters`,
 * everything else via `purchasing.services.dashboard_po_filters`.
 *
 * @param filters - the dashboard filter state
 * @param orderType - the dashboard's order-type toggle; `all` sends nothing
 * @returns query params for the drill-down API
 */
export function defaultTabDrilldownFilters(
  filters: FilterState | undefined,
  orderType?: 'all' | 'scheduled' | 'spot',
): Record<string, string> {
  // CR-4886: include supplier_group so drilldowns scope the same way the
  // dashboard tile does (the Submitted Appeals popup was returning every
  // appeal regardless of the Supplier Group selection without this).
  return {
    ...(filters?.dateFrom && { date_from: filters.dateFrom }),
    // W3-T5: the Historical/Live switch decides the arrears term AND caps the
    // window; the drill must read the same term and window as the ring it
    // opens from (date_to, data_mode) — one helper, every tab.
    ...drilldownModeParams(filters),
    ...(filters?.supplier && { supplier: filters.supplier }),
    ...(filters?.supplierGroup && { supplier_group: filters.supplierGroup }),
    ...(filters?.customer && { customer: filters.customer }),
    ...(filters?.site && { site: filters.site }),
    // CR-7229: carry the warehouse scope into the drill-downs so the detail
    // list matches the tile it was opened from.
    ...(filters?.warehouse && { warehouse: filters.warehouse }),
    ...(filters?.siteCountry && { site_country: filters.siteCountry }),
    ...(filters?.supplierCountry && { supplier_country: filters.supplierCountry }),
    // CR-6354: forward Strategic Suppliers Only so drill-downs match the
    // filtered tiles (backend reads strategic_only).
    ...(filters?.strategicOnly && { strategic_only: 'true' }),
    // Customer review 11 Sep (item 34): Internal / External Suppliers
    // reaches the drill-downs too — the OTIF Analysis grid listed external
    // suppliers under "Internal Suppliers".
    ...(filters?.supplierType && { supplier_type: filters.supplierType }),
    // Forward the call-off basis so a drill-down lists the same lines the
    // tile counted — otherwise the OTIF ring and its line list disagree.
    ...(filters?.callOffWarehouseBasis && { call_off_warehouse_basis: 'true' }),
    // CR-7776 / CR-7751: the tile's PO-header filters.
    ...(orderType && orderType !== 'all' ? { order_type: orderType } : {}),
    ...(filters?.commodityGroup && { lines__commodity_group: filters.commodityGroup }),
    ...(filters?.commodityType && { lines__commodity_type: filters.commodityType }),
    ...(filters?.supplierCode && { supplier_code: filters.supplierCode }),
  };
}

/**
 * CR-5557: human-readable label for the active timeframe so users can
 * validate what window the KPIs cover. Empty filters -> "All time".
 *
 * @param from - window start (YYYY-MM-DD)
 * @param to - window end (YYYY-MM-DD)
 * @returns e.g. "1 Oct 2025 – 30 Sept 2026", "From …", "Up to …" or "All time"
 */
export function formatPeriodLabel(from?: string, to?: string): string {
  if (!from && !to) return 'All time';
  const fmt = (s?: string) => {
    if (!s) return '';
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  };
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  return `Up to ${fmt(to)}`;
}
